import {
  getBillingMode,
  selectAllowUpgrade,
  selectStpRates,
  useIsWebsiteSelectorEnabled,
} from '@va/aaas/shared/selectors';
import { AaasBillingMode } from '@va/aaas/shared/types';
import { isCompanyRevealFeatureEnabled as isCompanyRevealFeatureEnabledSelector } from '@va/dashboard/selectors/app';
import { getWebsiteUrl, websiteRoleSelectors } from '@va/dashboard/selectors/core';
import { Checkmark, CoinsIcon, FolderIcon, StarV2 } from '@va/icons';
import { useLocalizationContext } from '@va/localization';
import {
  ChoiceSeparator,
  CircularProgress,
  fontWeights,
  Paragraph,
  paragraphSizes,
  TabbedButton,
} from '@va/ui/design-system';
import { addNumberSeparator } from '@va/util/helpers';
import classNames from 'classnames';
import { useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';
import { useAccountWebsitesStpData } from './account-stp-data/useAccountWebsitesStpData';
import { useCurrentWebsiteStpData } from './account-stp-data/useCurrentWebsiteStpData';
import { ConsumedTouchpointRow } from './ConsumedTouchpointRow';
import { CurrentPlan } from './CurrentPlan';
import { UpgradeButton } from './UpgradeButton';
import { WebsiteConsumptionRow } from './WebsiteConsumptionRow';

enum TabNames {
  CurrentWebsite,
  AllWebsites,
}

export const AccountStpCounterContent = () => {
  const websiteUrl = useSelector(getWebsiteUrl);
  const billingMode = useSelector(getBillingMode);
  const allowUpgrade = useSelector(selectAllowUpgrade);
  const { locale, translate } = useLocalizationContext();
  const { stpConsumed: websiteConsumed, pool } = useSelector(selectStpRates);
  const isCompanyRevealFeatureEnabled = useSelector(isCompanyRevealFeatureEnabledSelector);

  const hasInfiniteCredits = useMemo(() => pool?.limit === -1, [pool?.limit]);
  const poolLimit = useMemo(() => pool?.limit ?? 0, [pool?.limit]);
  const poolConsumed = useMemo(() => pool?.consumed ?? 0, [pool?.consumed]);

  const [selectedTab, setSelectedTab] = useState(TabNames.CurrentWebsite);
  const isCurrentWebsiteRatesTab = useMemo(() => selectedTab === TabNames.CurrentWebsite, [selectedTab]);
  const isInternalUpgradeEnabled = useMemo(() => billingMode === AaasBillingMode.PlatformManaged, [billingMode]);

  const currentWebsiteStpData = useCurrentWebsiteStpData();

  const enabled = useIsWebsiteSelectorEnabled();
  const isWebsiteContributor = useSelector(websiteRoleSelectors.isWebsiteContributor);

  const tabButtons = useMemo(() => {
    const items = [{ value: TabNames.CurrentWebsite, label: translate('label.currentWebsite') }];

    if (enabled && !isWebsiteContributor) {
      items.push({ value: TabNames.AllWebsites, label: translate('label.allWebsites') });
    }
    return items;
  }, [enabled, isWebsiteContributor, translate]);

  return (
    <div className='bg-black-lighter-84 rounded-24 p-3 text-white-83'>
      <CurrentPlan label={translate('creditsCounter.accountPlan')} />
      {(allowUpgrade || isInternalUpgradeEnabled) && (
        <UpgradeButton
          disabled={isWebsiteContributor}
          tooltip={isWebsiteContributor && translate('tooltips.contributorRole')}
          label={translate('creditsCounter.upgradeAccountPlan')}
        />
      )}
      <div>
        <TabbedButton
          buttonOptions={tabButtons}
          selectedValue={selectedTab}
          onChange={setSelectedTab}
          className='bg-black-dark! my-3'
          textClassName='text-white underline underline-offset-4'
          activeButtonClassName='bg-white-25!'
          inactiveButtonClassName='hover:bg-transparent active:bg-transparent'
          size='small'
        />
      </div>
      <div className='flex flex-row items-center gap-2.25 px-2'>
        <CoinsIcon color='#fff' />
        <div className='flex flex-col'>
          <div className='flex items-center gap-1 leading-18'>
            <Paragraph size={paragraphSizes.tiny} weight={fontWeights.medium} className='leading-18'>
              {translate('pageHeader.creditsCounter.accountCreditsLeft')}
            </Paragraph>
            &#183;
            <a href={translate('pageHeader.creditsCounter.howItWorksLink')} target='_blank' rel='noreferrer'>
              <Paragraph
                size={paragraphSizes.tiny}
                className='underline'
                colorClassName='text-blue-jordy'
                weight={fontWeights.medium}
              >
                {translate('label.howItWorks')}:
              </Paragraph>
            </a>
          </div>
          <Paragraph
            colorClassName={classNames({
              'text-orange-dark': hasInfiniteCredits,
            })}
          >
            {hasInfiniteCredits
              ? '∞'
              : translate('feature.counter.counting', {
                  behaviourCount: addNumberSeparator(poolLimit - poolConsumed, locale),
                  featureLimit: addNumberSeparator(poolLimit, locale),
                })}
          </Paragraph>
        </div>
        {!hasInfiniteCredits && (
          <div className='ml-auto'>
            <CircularProgress
              radius={15}
              stroke={4}
              progress={(poolConsumed / poolLimit) * 100}
              gradientColor='#F66F1E'
            />
          </div>
        )}
      </div>
      <ChoiceSeparator
        className='my-3! gap-18px!'
        labelClassName='text-white-83 leading-18!'
        label={translate('pageHeader.creditsCounter.creditsConsumed')}
      />
      {isCurrentWebsiteRatesTab && (
        <div className='flex flex-col gap-3 px-2'>
          <WebsiteConsumptionRow consumption={websiteConsumed} limit={poolLimit} domain={websiteUrl} />
          {currentWebsiteStpData.map(
            ({ icon, stpConsumed, stpConsumedLabel, hidden, stpConsumedLabelTooltip }, index) => (
              <ConsumedTouchpointRow
                key={index}
                label={stpConsumedLabel}
                count={stpConsumed}
                icon={icon}
                labelTooltip={stpConsumedLabelTooltip}
                hidden={hidden}
              />
            ),
          )}
          {isCompanyRevealFeatureEnabled && (
            <ConsumedTouchpointRow
              label={translate('featureCounter.companyReveal')}
              rightIcon={<Checkmark color='#FFF' />}
              icon={<FolderIcon color='#ffffffd4' />}
            />
          )}
          <ConsumedTouchpointRow
            label={translate('featureCounter.premiumSupport')}
            rightIcon={<Checkmark color='#FFF' />}
            icon={<StarV2 color='#ffffffd4' stroke='#ffffffd4' />}
          />
        </div>
      )}
      {!isCurrentWebsiteRatesTab && <AllWebsitesTab poolLimit={poolLimit} />}
    </div>
  );
};

const AllWebsitesTab = ({ poolLimit }: { poolLimit: number }) => {
  const {
    allWebsitesStpData: { items, isValidating, lastElementRef },
  } = useAccountWebsitesStpData();

  return (
    <div className='flex flex-col gap-2 px-2 h-60.5 overflow-auto scrollbar scrollbar-thumb'>
      {items?.map(({ consumed, domain, id }, index) => {
        if (index === items.length - 1) {
          return (
            <WebsiteConsumptionRow
              key={id}
              consumption={consumed}
              domain={domain}
              limit={poolLimit}
              ref={lastElementRef}
            />
          );
        }

        return <WebsiteConsumptionRow key={id} consumption={consumed} domain={domain} limit={poolLimit} />;
      })}
      {isValidating && <Skeleton count={1} height={42} inline baseColor='#3C3C3C' highlightColor='#000000D6' />}
    </div>
  );
};
